import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Route } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/route.interface';
import { ResultButton } from '../../../shared/detailed-result-buttons/detailed-result-buttons.component';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { SpecialMessagesPopUpComponent } from 'src/app/@shared/components/pop-ups/special-messages/special-messages-pop-up.component';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { UtilsService } from 'src/app/@shared/services/utils/utils.service';
import { LineRatingComponent } from '../../../shared/pop-ups/line-rating/line-rating.component';
import { debounceTime, skip } from 'rxjs';
import { Stop } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/stop.interface';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { Language, LanguageService } from 'src/app/@shared/services/language/language.service';
import {
  ACCESSIBLE_LINE,
  ACCESSIBLE_LINE_CALL_NEEDED,
  ALREADY_RATED,
  DAY,
  FLEXIBLE_SERVICE,
  LINE,
  MY_LOCATION,
  PICK_DAY_AND_TIME,
  RATE,
  RATE_LINE,
  RESERVE_IS_POSSIBLE,
  SHOW_PRICE,
  SIGN_UP_FOR_REPORTS,
  SIGN_UP_FOR_REPORTS_EXTERNAL_HINT,
  SIGN_UP_FOR_REPORTS_URL,
  SPECIAL_DATA_TEXTS,
  SPECIAL_MESSAGES,
  specialMessagesButtonAriaLabel,
  START_TRACK,
  STOPS_CHANGED_FOR_REQUESTED_DATE,
  TIME,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { Direction } from '@angular/cdk/bidi';
import { TariffProfile } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/tariff-profile.interface';
import { AutoCompleteService } from 'src/app/modules/route-planner/services/auto-complete/auto-complete.service';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { Coordinate } from 'src/app/modules/route-planner/interfaces/location/coordinate.interface';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { Rating } from 'src/app/modules/route-planner/interfaces/route-planning/rating.interface';
import { ResizeableComponent } from 'src/app/@shared/components/design/resizeable/resizeable.component';
import { RatingService } from 'src/app/modules/route-planner/services/rating/rating.service';
import { UserLocationService } from 'src/app/@shared/services/user-location/user-location.service';
import { LocationService } from 'src/app/modules/route-planner/services/location/location.service';

@Component({
    selector: 'app-search-line-detailed-result',
    templateUrl: './search-line-detailed-result.component.html',
    styleUrls: ['./search-line-detailed-result.component.scss'],
    standalone: false
})
export class SearchLineDetailedResultComponent
  extends ResizeableComponent
  implements OnInit {
  @Output() onCalendarPickChange = new EventEmitter<{
    day: Date;
    time: string;
  }>();
  @Output() onDayPickChange = new EventEmitter<Date>();
  @Output() onTariffProfileChange = new EventEmitter<TariffProfile>();
  @Output() onToggleRoutePrice = new EventEmitter<boolean>();
  @Output() closeCardEvent = new EventEmitter<void>();
  @Output() navigateToRouteEvent = new EventEmitter<boolean>();

  @Input() line: Route;
  @Input() boardingStation: Stop;
  @Input() dropingStation: Stop;
  @Input() stopsChanged: boolean = false;

  tariffProfileFormControl = new FormControl<TariffProfile>(null);
  priceCalculationLabel: string
  showRoutePrice: boolean = false;

  calendarPickForm: FormGroup;

  minDate: Date = new Date();
  maxDate: Date = new Date(new Date().setDate(new Date().getDate() + 6));

  pickDayAndTimeText: string;
  timeText: string;
  dayText: string;
  rateText: string;
  startTackText: string;
  alreadyRatedText: string;
  stopsChangedText: string;

  myLocationText: string;
  userLocation: Coordinate;

  direction: Direction;

  isRatedLine: boolean;

  resultButtons: ResultButton[];

  constructor(
    protected override readonly resizeService: ResizeService,

    public readonly routePlanningService: RoutePlanningService,
    public readonly utilsService: UtilsService,

    private readonly userLocationService: UserLocationService,
    private readonly ratingService: RatingService,
    private readonly modalService: ModalService,
    private readonly queryParamsService: QueryParamsService,
    public readonly languageService: LanguageService,
    private readonly locationService: LocationService,
    private readonly autoCompleteService: AutoCompleteService,
    fb: FormBuilder
  ) {
    super(resizeService);

    this.calendarPickForm = fb.group({
      day: new FormControl<Date>(new Date()),
      time: new FormControl<string>(this.utilsService.getCurrentTime()),
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initButtons(this.languageService.getSelectedLanguage());

    if(!this.showRoutePrice) {
      this.tariffProfileFormControl.disable();
    }

    let ratedLines = JSON.parse(localStorage.getItem('ratedLineId') || '[]');
    let ratedLine = ratedLines.find(item => item.value === this.line.routeId.toString());
    if (ratedLine) {
      if (ratedLine.expiration <= Date.now()) {
        ratedLines = ratedLines.filter(item => item.value !== this.line.routeId.toString());
        localStorage.setItem('ratedLineId', JSON.stringify(ratedLines));
        ratedLine = null;
      }
    }
    this.isRatedLine = !!ratedLine;

    this.calendarPickForm.valueChanges
      .pipe(
        skip(1),
        debounceTime(AutoCompleteService.AUTO_COMPLETE_DEBOUNCE_TIME)
      )
      .subscribe((val) => {
        this.onCalendarPickChange.emit(val);
      });

    this.calendarPickForm
      .get('day')
      .valueChanges.pipe(skip(1))
      .subscribe((day) => {
        this.onDayPickChange.emit(day);
      });

    this.tariffProfileFormControl.valueChanges.subscribe((val) =>
      this.onTariffProfileChange.emit(val)
    );

    this.ratingService.onRatingsChange$.subscribe((ratings) => {
      this.isRatedLine = !!ratings[this.line.routeId];
    });

    this.userLocationService.consentGranted$.subscribe((consent) => {
      if (consent) {
        this.userLocation = this.userLocationService.userLocation;
      }
    });

    this.initializeMultiLanguageTexts();
  }

  initButtons(language: Language) {

    this.resultButtons = [
      {
        id: 'specialMessages',
        name: 'specialMessages',
        label: SPECIAL_MESSAGES[language.language],
        icon: 'message-exclamation',
        ariaHaspopup: 'dialog',
        onClick: () => {
          this.modalService.openPopUp(SpecialMessagesPopUpComponent, {
            data: { messages: this.line.specialMessages },
          });
        },
        show: this.line.specialMessages?.length > 0,
        counter: new Set(this.line.specialMessages?.map(m => m.messageId)).size,
        ariaLabel: specialMessagesButtonAriaLabel(
          language.language,
          SPECIAL_MESSAGES[language.language],
          this.line.specialMessages?.length ?? 0
        ),
      },
      {
        id: 'specialData.isAccessible',
        label: this.line.specialData?.isCallNeeded ? ACCESSIBLE_LINE_CALL_NEEDED[language.language] : ACCESSIBLE_LINE[language.language],
        icon: this.line.specialData?.isCallNeeded ? 'wheelchair-move-phone' : 'wheelchair-move',
        ariaHaspopup: 'dialog',
        onClick: () => {
          this.modalService.openAgencyContactInfoModal(this.line.agencyId, this.line.specialData?.isCallNeeded ? true : false);
        },
        show: this.line.specialData?.isAccessible,
        tooltip: this.line.specialData?.isCallNeeded ? SPECIAL_DATA_TEXTS.accessibleWithOrderTooltip[language.language] : SPECIAL_DATA_TEXTS.accessibleTooltip[language.language]
      },

      {
        id: 'specialData.isFlexible',
        label: FLEXIBLE_SERVICE[language.language],
        icon: 'phone',
        ariaHaspopup: 'dialog',
        onClick: () => {
          this.modalService.openAgencyContactInfoModal(this.line.agencyId, false);
        },
        show: this.line.specialData?.isFlexible,
        tooltip: SPECIAL_DATA_TEXTS.flexibleTooltip[language.language]
      },
      {
        id: 'specialData.isOrderInAdvanced',
        label: RESERVE_IS_POSSIBLE[language.language],
        icon: 'phone',
        ariaHaspopup: 'dialog',
        onClick: () => {
          this.modalService.openAgencyContactInfoModal(this.line.agencyId, true);
        },
        show: this.line.specialData?.isOrderInAdvanced,
      },
      {
        label: SIGN_UP_FOR_REPORTS[language.language],
        icon: 'mobile-signal',
        name: 'signUpReports',
        show: true,
        externalHref: SIGN_UP_FOR_REPORTS_URL,
        externalLinkHint:
          SIGN_UP_FOR_REPORTS_EXTERNAL_HINT[language.language],
      },
    ].filter(button => button.show);
  };

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.direction = language.direction;
      this.pickDayAndTimeText = PICK_DAY_AND_TIME[language.language];
      this.timeText = TIME[language.language];
      this.dayText = DAY[language.language];
      this.rateText = RATE_LINE[language.language];
      this.startTackText = START_TRACK[language.language];
      this.myLocationText = MY_LOCATION[language.language];
      this.priceCalculationLabel = SHOW_PRICE[language.language];
      this.alreadyRatedText = ALREADY_RATED[language.language].replace(
        '{0}',
        LINE[language.language].toLowerCase()
      );
      this.stopsChangedText = STOPS_CHANGED_FOR_REQUESTED_DATE[language.language]
      this.initButtons(language);
    });
  }

  openLineRatingPopUp(rating: Rating) {
    this.modalService
      .openPopUp(LineRatingComponent, {
        data: {
          entityId: this.line.routeId,
          rating: rating,
          entityType: 1,
        },
      })
      .subscribe((newRating: Rating) => {
        if (!!newRating) {
          if (newRating.ratingsCount >= 5) {
            this.line.rating = newRating;
          }
          this.isRatedLine = true;
          const expirationDate = new Date();
          expirationDate.setHours(expirationDate.getHours() + 12); // Expires in 12 hours
          const item = {
            value: this.line.routeId.toString(),
            expiration: expirationDate.getTime()
          };

          let ratedLines = JSON.parse(localStorage.getItem('ratedLineId') || '[]');
          ratedLines.push(item);
          localStorage.setItem('ratedLineId', JSON.stringify(ratedLines));
        }
      });
  }

  navigateToLine() {
    const params = {
      dll: `${this.dropingStation.lat}_${this.dropingStation.lng}`,
      dest: `${this.dropingStation.name[this.languageService.getSelectedLanguage().symbol]} - ${this.dropingStation.stopcode}`,
      saveParams: true,
    };

    // If we have user location, try to get the address data first
    if (this.userLocation) {
      this.navigateToRouteEvent.emit(true)
      // this.autoCompleteService
      //   .getAddressDataByCoordinates(this.userLocation)
      //   .subscribe({
      //     next: (response) => {
      //       if (response.success && response.data) {
      //         // On success, use the address label
      //         params['org'] = response.data.addressDetails.label;
      //         params[
      //           'oll'
      //         ] = `${this.userLocation.lat}_${this.userLocation.lng}`;
      //       } else {
      //         // On failed response, use default "My Location" text
      //         params['org'] = this.myLocationText;
      //         params[
      //           'oll'
      //         ] = `${this.userLocation.lat}_${this.userLocation.lng}`;
      //       }

      //       // Navigate after we get the results
      //       this.navigateWithParams(params);
      //     },
      //     error: () => {
      //       // On error, use default "My Location" text
      //       params['org'] = this.myLocationText;
      //       params['oll'] = `${this.userLocation.lat}_${this.userLocation.lng}`;

      //       // Navigate even on error
      //       this.navigateWithParams(params);
      //     },
      //   });
      params['org'] = this.myLocationText;
      params[
        'oll'
      ] = `${this.userLocation.lat}_${this.userLocation.lng}`;
      this.navigateWithParams(params);

    } else {
      // If no user location, just navigate with the base parameters

      this.navigateWithParams(params);
    }
  }

  // Helper method to navigate with parameters
  private navigateWithParams(params: any) {
    this.navigateToRouteEvent.emit(false)
    this.routePlanningService._mapOverlaySub.next(null);
    this.closeCardEvent.emit();
    // Clear tab query parameter and set to SOURCE_DESTINATION
    this.queryParamsService.clearQueryParams(['tab']);
    this.queryParamsService.setQueryParam('tab', eTab.SOURCE_DESTINATION);

    // Set all query parameters at once
    this.queryParamsService.setQueryParams(params);
  }

  togglePriceCalculation(showPrice: boolean): void {
    this.showRoutePrice = !this.showRoutePrice;
    if (!!this.showRoutePrice) {
      this.tariffProfileFormControl.enable();
    } else {
      this.tariffProfileFormControl.disable();
    }
    this.onToggleRoutePrice.emit(this.showRoutePrice);
  }

}
